import React, { useEffect, useState } from 'react';
import {
  View, StyleSheet, Text, ActivityIndicator,
} from 'react-native';
import Modal from 'react-native-modal';
import Style from '../../../../constants/Style';
import Colors from '../../../../constants/Colors';
import ScoreSettings from './ScoreSettings';
import WorkingHoursSettings from './WorkingHoursSettings';
import PrioritySettings from './PrioritySettings';
import {
  DAYS, Day, BUILDING_SETTINGS_PRIORITIES, BuildingSettingsPriority, validatePriorityNames,
  validatePriorityDescriptions, validatePriorityTimes, validateWorkingDays, validateWorkingHours,
  validateResponseTimes, validateResolutionTimes, checkResponseTimeMinimum,
  checkResolutionTimeMinimum, checkResolutionTimeMaximum, checkResponseTimeMaximum,
} from './data';
import { Building } from '../AddBuildingModal';
import {
  ATLEAST_ONE_WORKING_DAY, EDIT_BUILDING_SETTINGS_FAILURE, MAXIMUM_RESOLUTION_TIME_ERROR,
  MAXIMUM_RESPONSE_TIME_ERROR, MINIMUM_RESOLUTION_TIME_ERROR, MINIMUM_RESPONSE_TIME_ERROR,
  PRIORITY_DESCRIPTION_REQUIRED, PRIORITY_NAME_REQUIRED, RESOLUTION_RESPONSE_TIME_ERROR,
  RESOLUTION_TIME_ORDER_ERROR, RESPONSE_TIME_ORDER_ERROR, WORKING_TIME_ERROR,
} from '../../../../constants/AlertMessages';
import updateBuildingSettings from '../../../../requests/Locations/Buildings/UpdateBuildingSettings';
import PressableStyled from '../../../../components/PressableStyled';
import RightPointer from '../../../../components/30pxIcons/RightPointer';
import CloseModalIcon from '../../../../components/CloseModalIcon';
import Button from '../../../../components/Button';
import AlertModal from '../../../../components/AlertModal';

// eslint-disable-next-line no-shadow, no-unused-vars
enum Option {
  // eslint-disable-next-line no-unused-vars
  SCORE, PRIORITY, HOURS,
}

type Category = {
  title: string,
  description: string,
  option: Option,
  testID: string,
}

const OPTIONS: Category[] = [
  // {
  //   title: 'Score settings',
  //   description: 'Customize score thresholds',
  //   option: Option.SCORE,
  // },
  {
    title: 'Working hours settings',
    description: 'Set working hours in your building',
    option: Option.HOURS,
    testID: 'working-hours-settings-btn',
  },
  {
    title: 'Priority settings',
    description: 'Set priority, adjust response and resolution hours',
    option: Option.PRIORITY,
    testID: 'priority-settings-btn',
  },
];

interface BuildingSettingsModalProps {
  selectedBuilding: Building,
  buildingSettingsShown: boolean,
  hideBuildingSettingsModal: () => void,
  editBuilding,
  showScreenAlert,
}

const BuildingSettingsModal = ({
  buildingSettingsShown, hideBuildingSettingsModal, selectedBuilding, editBuilding, showScreenAlert,
}: BuildingSettingsModalProps) => {
  const [selectedCategory, setSelectedCategory] = useState<Category>(null);
  const [visibilityToggleEnabled, setVisibilityToggleEnabled] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [dayList, setDayList] = useState<Day[]>((selectedBuilding
    ?.WorkingHours ?? DAYS).map((day: Day) => ({ ...day })));
  const [priorityList, setPriorityList] = useState<BuildingSettingsPriority[]>(
    JSON.parse(JSON.stringify((selectedBuilding?.SlaHours ?? BUILDING_SETTINGS_PRIORITIES)
      .map((priority: BuildingSettingsPriority) => ({ ...priority })))),
  );
  const [loading, setLoading] = useState<boolean>(false);

  const showAlert = (
    title: string,
    message: string,
    error: boolean = true,
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setLoading(false);
    setAlertVisible(true);
  };

  const resetData = () => {
    setDayList((selectedBuilding?.WorkingHours ?? DAYS).map((day: Day) => ({ ...day })));
    setPriorityList(
      JSON.parse(JSON.stringify((selectedBuilding?.SlaHours ?? BUILDING_SETTINGS_PRIORITIES)
        .map((priority: BuildingSettingsPriority) => ({ ...priority })))),
    );
  };

  useEffect(() => {
    if (buildingSettingsShown) {
      setVisibilityToggleEnabled(false);
      const timeout = setTimeout(() => {
        setVisibilityToggleEnabled(true);
        clearTimeout(timeout);
      }, 500);
      setSelectedCategory(null);
    }
  }, [buildingSettingsShown]);

  const closeModal = () => (visibilityToggleEnabled ? hideBuildingSettingsModal() : null);

  const back = () => {
    resetData();
    setSelectedCategory(null);
  };

  const updateError = (errorText?: string) => showAlert(
    'Error',
    errorText ?? EDIT_BUILDING_SETTINGS_FAILURE,
  );

  const save = () => {
    const errorMessages: string[] = [];
    if (selectedCategory?.option === Option.HOURS) {
      if (!validateWorkingDays(dayList)) {
        errorMessages.push(`- ${ATLEAST_ONE_WORKING_DAY}`);
      }
      if (!validateWorkingHours(dayList)) {
        errorMessages.push(`- ${WORKING_TIME_ERROR}`);
      }
    } else if (selectedCategory?.option === Option.PRIORITY) {
      if (!validatePriorityNames(priorityList)) {
        errorMessages.push(`- ${PRIORITY_NAME_REQUIRED}`);
      }
      if (!validatePriorityDescriptions(priorityList)) {
        errorMessages.push(`- ${PRIORITY_DESCRIPTION_REQUIRED}`);
      }
      if (!validatePriorityTimes(priorityList)) {
        errorMessages.push(`- ${RESOLUTION_RESPONSE_TIME_ERROR}`);
      }
      if (!validateResponseTimes(priorityList)) {
        errorMessages.push(`- ${RESPONSE_TIME_ORDER_ERROR}`);
      }
      if (!validateResolutionTimes(priorityList)) {
        errorMessages.push(`- ${RESOLUTION_TIME_ORDER_ERROR}`);
      }
      priorityList.forEach((priority: BuildingSettingsPriority) => {
        if (!checkResponseTimeMinimum(priority)) {
          errorMessages.push(`- ${MINIMUM_RESPONSE_TIME_ERROR}`);
        }
        if (!checkResolutionTimeMinimum(priority)) {
          errorMessages.push(`- ${MINIMUM_RESOLUTION_TIME_ERROR}`);
        }
        if (!checkResponseTimeMaximum(priority)) {
          errorMessages.push(`- ${MAXIMUM_RESPONSE_TIME_ERROR}`);
        }
        if (!checkResolutionTimeMaximum(priority)) {
          errorMessages.push(`- ${MAXIMUM_RESOLUTION_TIME_ERROR}`);
        }
      });
    }
    if (errorMessages.length > 0) showAlert('Error', Array.from(new Set(errorMessages)).join('\n'));
    else {
      setLoading(true);
      const updatedBuilding: Building = {
        ...selectedBuilding,
        SlaHours: priorityList,
        WorkingHours: dayList,
        LocationCapacity: selectedBuilding.LocationCapacity,
      };
      updateBuildingSettings(updatedBuilding).then((res) => {
        if (res && !res.isError) {
          setLoading(false);
          editBuilding(updatedBuilding);
          hideBuildingSettingsModal();
          showScreenAlert(
            'Success',
            <Text>
              {'The settings for '}
              <Text style={Style.alertBoldText}>
                {selectedBuilding.Name}
              </Text>
              {' was successfully updated'}
            </Text>,
            false,
          );
        } else updateError(res?.errorText);
      }).catch((error) => updateError(error?.message));
    }
  };

  const OptionItem = (category: Category) => <PressableStyled
    onPress={() => setSelectedCategory(category)}
    style={styles.optionItem}
    testID={category.testID}
  >
    <View>
      <Text style={styles.optionTitle}>{category.title}</Text>
      <Text style={styles.optionDescription}>{category.description}</Text>
    </View>
    <RightPointer />
  </PressableStyled>;

  return <Modal
    isVisible={buildingSettingsShown}
    onBackButtonPress={hideBuildingSettingsModal}
    onBackdropPress={closeModal}
    onSwipeComplete={closeModal}
    onModalHide={resetData}
    onModalWillShow={resetData}
    style={Style.modal}
  >
    <View style={[
      styles.container, Style.modalContent,
      selectedCategory?.option === Option.PRIORITY && styles.containerPriority,
    ]}>
      <PressableStyled
        style={Style.closeButton}
        onPress={hideBuildingSettingsModal}
        testID='lm-close-building-settings-btn'
      >
        <CloseModalIcon />
      </PressableStyled>
      <Text style={styles.title}>{selectedCategory?.title ?? 'Building settings'}</Text>
      {selectedCategory?.option === Option.SCORE && <ScoreSettings />}
      {selectedCategory?.option === Option.PRIORITY
        && <PrioritySettings {...{ priorityList, setPriorityList }} />}
      {selectedCategory?.option === Option.HOURS
        && <WorkingHoursSettings {...{ dayList, setDayList }} />}
      {!selectedCategory ? <>
        {OPTIONS.map((option: Category) => <OptionItem
          {...{ ...option }}
          key={`option_${option.option}`}
        />)}
      </> : <View style={[Style.row, styles.buttonContainer]}>
        <Button
          title='Back'
          onPress={back}
          testID='back-btn'
        />
        <Button
          affirmative
          title='Save'
          onPress={save}
          testID='save-btn'
        />
      </View>}
    </View>
    <ActivityIndicator
      size='large'
      animating={loading}
      style={Style.activityIndicator}
    />
    <AlertModal
      modalVisible={alertVisible}
      setModalVisible={setAlertVisible}
      title={alertTitle}
      message={alertMessage}
      error={alertError}
    />
  </Modal>;
};

const styles = StyleSheet.create({
  container: {
    width: 530,
    borderRadius: 10,
    paddingHorizontal: 40,
    paddingTop: 60,
    paddingBottom: 32,
    alignSelf: 'center',
    maxHeight: '90%',
  },
  containerPriority: {
    width: 1012,
    maxWidth: '90%',
  },
  title: {
    fontSize: 28,
    lineHeight: 35,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_600SemiBold',
    alignSelf: 'center',
    textAlign: 'center',
    marginBottom: 32,
  },
  optionItem: {
    paddingVertical: 16,
    paddingHorizontal: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderRadius: 5,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    backgroundColor: Colors.light.hereworksWhite200,
    marginBottom: 8,
  },
  optionTitle: {
    fontSize: 20,
    lineHeight: 30,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
  },
  optionDescription: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  buttonContainer: {
    alignSelf: 'center',
    marginTop: 40,
    marginBottom: 8,
  },
});

export default BuildingSettingsModal;
